// src/features/legalentity/PaymentMethodsPanel.tsx
// Payment methods attached to this legal entity, and contract-ready bank
// requisites resolved through it — both live in PaymentService, not
// LegalEntityService, so this panel calls the payment domain's endpoints
// directly. Additive to LegalEntityDetailPage: sits next to the entity's own
// BankAccountsPanel in the banking/requisites section.
//
// ListPaymentMethodsByLegalEntity resolves PaymentMethodDetail rows attached
// directly or transitively (via BillingAccount.legal_entity_ref).
// GetContractRequisitesForLegalEntity resolves the single bank block a
// contract/signature page should render, optionally scoped to a currency.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { errMessage } from './lib';
import { ListPaymentMethodsByLegalEntity } from '@core/api/endpoints/payment/list_payment_methods_by_legal_entity';
import { GetContractRequisitesForLegalEntity } from '@core/api/endpoints/payment/get_contract_requisites_for_legal_entity';
import type { PaymentMethodDetail } from '@core/types/payment/PaymentMethodDetail';
import type { BankAccountRequisites } from '@core/types/payment/BankAccountRequisites';
import type { Entity } from '@core/types/entity';

interface Props {
  legalEntityId: string;
}

function methodLabel(m: PaymentMethodDetail): string {
  if (m.card_brand || m.card_last_four) return `${m.card_brand || 'card'} •••• ${m.card_last_four}`;
  if (m.bank_name || m.bank_account_last_four) return `${m.bank_name || 'bank'} •••• ${m.bank_account_last_four}`;
  return m.type || m.entity?.id || '-';
}

export function PaymentMethodsPanel({ legalEntityId }: Props) {
  const { t } = useI18n();

  const [methods, setMethods] = useState<PaymentMethodDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currency, setCurrency] = useState('');
  const [requisites, setRequisites] = useState<BankAccountRequisites | null>(null);
  const [requisitesRefs, setRequisitesRefs] = useState<{ billingAccount?: Entity; paymentMethod?: Entity } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    if (!legalEntityId) return;
    setIsLoading(true);
    setError(null);
    ListPaymentMethodsByLegalEntity(legalEntityId)
      .then((res) => setMethods(res.payment_methods?.methods ?? []))
      .catch((e) => setError(errMessage(e, t('legalentity.paymentMethods.loadError'))))
      .finally(() => setIsLoading(false));
  }, [legalEntityId, t]);

  const resolveRequisites = async () => {
    setResolving(true);
    setResolveError(null);
    try {
      const res = await GetContractRequisitesForLegalEntity(legalEntityId);
      setRequisites(res.requisites ?? null);
      setRequisitesRefs({ billingAccount: res.billing_account, paymentMethod: res.payment_method });
      if (!res.requisites) setResolveError(t('legalentity.paymentMethods.noRequisites'));
    } catch (e) {
      setResolveError(errMessage(e, t('legalentity.paymentMethods.resolveError')));
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Payment methods attached to this legal entity */}
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('legalentity.paymentMethods.title')}
        </h3>
        {error && <Alert variant="error">{error}</Alert>}
        {isLoading ? (
          <div className="h-12 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : methods.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('legalentity.paymentMethods.empty')}</p>
        ) : (
          <div className="space-y-1.5">
            {methods.map((m) => (
              <div key={m.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-sm">
                <div>
                  <p className="font-medium text-foreground">
                    {methodLabel(m)}
                    {m.is_default && <span className="ml-2 text-xs text-muted-foreground">({t('legalentity.paymentMethods.default')})</span>}
                  </p>
                  {m.bank_requisites?.currency && (
                    <p className="text-xs text-muted-foreground">{m.bank_requisites.currency}</p>
                  )}
                </div>
                {!m.is_valid && <span className="text-xs text-destructive">{m.failure_reason || t('legalentity.paymentMethods.invalid')}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contract-ready bank requisites, resolved on demand */}
      <div className="space-y-2 rounded-md border border-dashed border-border p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('legalentity.paymentMethods.requisitesTitle')}
        </h3>
        <p className="text-xs text-muted-foreground">{t('legalentity.paymentMethods.requisitesHint')}</p>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            label={t('legalentity.paymentMethods.currencyFilter')}
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
            placeholder="USD"
          />
          <Button size="sm" onClick={resolveRequisites} isLoading={resolving}>
            {t('legalentity.paymentMethods.resolveCta')}
          </Button>
        </div>
        {resolveError && <Alert variant="error">{resolveError}</Alert>}
        {requisites && (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex justify-between sm:block">
              <dt className="text-muted-foreground">{t('legalentity.paymentMethods.field.accountHolder')}</dt>
              <dd className="text-foreground">{requisites.account_holder_name || '-'}</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-muted-foreground">{t('legalentity.paymentMethods.field.bankName')}</dt>
              <dd className="text-foreground">{requisites.bank_name || '-'}</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-muted-foreground">{t('legalentity.paymentMethods.field.iban')}</dt>
              <dd className="font-mono text-foreground">{requisites.iban || requisites.account_number || '-'}</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-muted-foreground">{t('legalentity.paymentMethods.field.swift')}</dt>
              <dd className="font-mono text-foreground">{requisites.swift_bic || '-'}</dd>
            </div>
            <div className="flex justify-between sm:block">
              <dt className="text-muted-foreground">{t('legalentity.paymentMethods.field.currency')}</dt>
              <dd className="text-foreground">{requisites.currency || '-'}</dd>
            </div>
            {requisitesRefs?.billingAccount?.id && (
              <div className="flex justify-between sm:block">
                <dt className="text-muted-foreground">{t('legalentity.paymentMethods.field.billingAccount')}</dt>
                <dd className="font-mono text-xs text-foreground">{requisitesRefs.billingAccount.id.slice(0, 8)}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </div>
  );
}
